<script setup lang="ts">
import { computed, ref } from 'vue'
import { ChevronDownIcon, ChevronRightIcon, DocumentTextIcon } from '@heroicons/vue/24/outline'
import type { BillingStatus, PaymentStatus, QuoteBillingRow } from '@/stores/useQuoteBillingDashboardStore'

const props = defineProps<{ rows: QuoteBillingRow[] }>()

// ── Vue : par devis (groupé) ou liste plate des factures ────────────────────

const viewMode = ref<'quotes' | 'invoices'>('quotes')

interface FlatInvoiceRow {
  uuid: string
  quoteUuid: string
  quoteReference: string
  companyName: string | null
  salesEngineerName: string | null
  name: string | null
  referenceExterne: string | null
  invoiceDate: string | null
  invoiceDateDue: string | null
  amountTotal: number
  moveType: string | null
  state: string | null
  paymentState: string | null
}

const flatInvoices = computed<FlatInvoiceRow[]>(() =>
  props.rows.flatMap((row) =>
    row.invoices.map((inv) => ({
      uuid: inv.uuid,
      quoteUuid: row.quoteUuid,
      quoteReference: row.quoteReference,
      companyName: row.companyName,
      salesEngineerName: row.salesEngineerName,
      name: inv.name,
      referenceExterne: inv.referenceExterne,
      invoiceDate: inv.invoiceDate,
      invoiceDateDue: inv.invoiceDateDue,
      amountTotal: inv.amountTotal,
      moveType: inv.moveType,
      state: inv.state,
      paymentState: inv.paymentState,
    })),
  ),
)

type InvoiceSortKey = 'companyName' | 'invoiceDate' | 'amountTotal' | 'paymentState'
const invoiceSortKey = ref<InvoiceSortKey | null>(null)
const invoiceSortDir = ref<'asc' | 'desc'>('asc')

function toggleInvoiceSort(key: InvoiceSortKey) {
  if (invoiceSortKey.value === key) {
    invoiceSortDir.value = invoiceSortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    invoiceSortKey.value = key
    invoiceSortDir.value = key === 'companyName' ? 'asc' : 'desc'
  }
}

function invoiceSortIndicator(key: InvoiceSortKey): string {
  if (invoiceSortKey.value !== key) return ''
  return invoiceSortDir.value === 'asc' ? ' ▲' : ' ▼'
}

const sortedFlatInvoices = computed(() => {
  if (!invoiceSortKey.value) return flatInvoices.value
  const dir = invoiceSortDir.value === 'asc' ? 1 : -1
  const key = invoiceSortKey.value
  return [...flatInvoices.value].sort((a, b) => {
    if (key === 'companyName') return (a.companyName ?? '').localeCompare(b.companyName ?? '') * dir
    if (key === 'paymentState') {
      return ((PAYMENT_STATE_RANK[a.paymentState ?? ''] ?? -1) - (PAYMENT_STATE_RANK[b.paymentState ?? ''] ?? -1)) * dir
    }
    if (key === 'invoiceDate') {
      const at = a.invoiceDate ? new Date(a.invoiceDate).getTime() : 0
      const bt = b.invoiceDate ? new Date(b.invoiceDate).getTime() : 0
      return (at - bt) * dir
    }
    return ((a.amountTotal ?? 0) - (b.amountTotal ?? 0)) * dir
  })
})

// ── Tri (clic sur en-tête) ───────────────────────────────────────────────────

type SortKey = 'companyName' | 'signedAt' | 'totalCa' | 'billedAmount' | 'billingStatus' | 'paymentStatus'

const sortKey = ref<SortKey | null>(null)
const sortDir = ref<'asc' | 'desc'>('asc')

// Ordre métier (pas alphabétique) : du moins avancé au plus avancé.
const BILLING_RANK: Record<BillingStatus, number> = { NOT_INVOICED: 0, PARTIALLY_INVOICED: 1, INVOICED: 2 }
const PAYMENT_RANK: Record<PaymentStatus, number> = { UNPAID: 0, PARTIALLY_PAID: 1, PAID: 2 }

function toggleSort(key: SortKey) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    // texte : A→Z par défaut ; montants/dates/statuts : décroissant
    sortDir.value = key === 'companyName' ? 'asc' : 'desc'
  }
}

function sortIndicator(key: SortKey): string {
  if (sortKey.value !== key) return ''
  return sortDir.value === 'asc' ? ' ▲' : ' ▼'
}

const sortedRows = computed(() => {
  if (!sortKey.value) return props.rows
  const dir = sortDir.value === 'asc' ? 1 : -1
  const key = sortKey.value
  return [...props.rows].sort((a, b) => {
    if (key === 'companyName') return (a.companyName ?? '').localeCompare(b.companyName ?? '') * dir
    if (key === 'signedAt') {
      const at = a.signedAt ? new Date(a.signedAt).getTime() : 0
      const bt = b.signedAt ? new Date(b.signedAt).getTime() : 0
      return (at - bt) * dir
    }
    if (key === 'billingStatus') return (BILLING_RANK[a.billingStatus] - BILLING_RANK[b.billingStatus]) * dir
    if (key === 'paymentStatus') return (PAYMENT_RANK[a.paymentStatus] - PAYMENT_RANK[b.paymentStatus]) * dir
    return ((a[key] ?? 0) - (b[key] ?? 0)) * dir
  })
})

// ── Lignes dépliables (factures liées) ──────────────────────────────────────

const expanded = ref<Set<string>>(new Set())
function toggle(quoteUuid: string) {
  const next = new Set(expanded.value)
  if (next.has(quoteUuid)) next.delete(quoteUuid)
  else next.add(quoteUuid)
  expanded.value = next
}

// ── Helpers d'affichage ───────────────────────────────────────────────────────

const BILLING_CONFIG: Record<BillingStatus, { label: string; class: string }> = {
  NOT_INVOICED: { label: 'Non facturé', class: 'badge-ghost' },
  PARTIALLY_INVOICED: { label: 'Partiel', class: 'badge-warning' },
  INVOICED: { label: 'Facturé', class: 'badge-success' },
}
const PAYMENT_CONFIG: Record<PaymentStatus, { label: string; class: string }> = {
  UNPAID: { label: 'Non payé', class: 'badge-error' },
  PARTIALLY_PAID: { label: 'Partiel', class: 'badge-warning' },
  PAID: { label: 'Payé', class: 'badge-success' },
}

// Alignés sur la règle métier du backend (PAID_STATES, quote-billing.service.ts) :
// in_payment et reversed comptent comme "Payée", pour ne pas contredire le badge du devis.
const PAYMENT_STATE_LABELS: Record<string, string> = {
  paid: 'Payée',
  not_paid: 'Non payée',
  partial: 'Partielle',
  in_payment: 'Payée',
  reversed: 'Payée',
}
const PAYMENT_STATE_RANK: Record<string, number> = {
  not_paid: 0,
  partial: 1,
  paid: 2,
  in_payment: 2,
  reversed: 2,
}

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function formatMoney(value: number | null) {
  if (value === null || value === undefined) return '—'
  return value.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })
}

function invoiceDateRange(row: { invoiceDateMin: string | null; invoiceDateMax: string | null }) {
  if (!row.invoiceDateMin) return '—'
  if (row.invoiceDateMin === row.invoiceDateMax) return formatDate(row.invoiceDateMin)
  return `${formatDate(row.invoiceDateMin)} → ${formatDate(row.invoiceDateMax)}`
}
</script>

<template>
  <div class="flex justify-end mb-2">
    <div class="join">
      <button
        class="join-item btn btn-xs"
        :class="viewMode === 'quotes' ? 'btn-primary' : 'btn-ghost'"
        @click="viewMode = 'quotes'"
      >
        Devis
      </button>
      <button
        class="join-item btn btn-xs"
        :class="viewMode === 'invoices' ? 'btn-primary' : 'btn-ghost'"
        @click="viewMode = 'invoices'"
      >
        Factures
      </button>
    </div>
  </div>

  <div v-if="viewMode === 'invoices'">
    <div v-if="flatInvoices.length" class="overflow-x-auto rounded-xl border border-base-300">
      <table class="table table-sm w-full">
        <caption class="sr-only">Factures rattachées aux devis signés</caption>
        <thead>
          <tr>
            <th>Facture</th>
            <th>Réf. devis</th>
            <th class="cursor-pointer select-none hover:bg-base-200" @click="toggleInvoiceSort('companyName')">Client{{ invoiceSortIndicator('companyName') }}</th>
            <th>Commercial</th>
            <th>Type</th>
            <th>État</th>
            <th class="cursor-pointer select-none hover:bg-base-200" @click="toggleInvoiceSort('invoiceDate')">Date{{ invoiceSortIndicator('invoiceDate') }}</th>
            <th>Échéance</th>
            <th class="text-right cursor-pointer select-none hover:bg-base-200" @click="toggleInvoiceSort('amountTotal')">Montant{{ invoiceSortIndicator('amountTotal') }}</th>
            <th class="cursor-pointer select-none hover:bg-base-200" @click="toggleInvoiceSort('paymentState')">Paiement{{ invoiceSortIndicator('paymentState') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="inv in sortedFlatInvoices" :key="inv.uuid">
            <td class="font-mono text-xs">
              <DocumentTextIcon class="size-3 inline mr-1 text-base-content/40" />{{ inv.name || inv.referenceExterne || '—' }}
            </td>
            <td class="font-mono text-xs">{{ inv.quoteReference }}</td>
            <td>{{ inv.companyName || '—' }}</td>
            <td>{{ inv.salesEngineerName || '—' }}</td>
            <td class="text-xs">{{ inv.moveType || '—' }}</td>
            <td class="text-xs">{{ inv.state || '—' }}</td>
            <td class="text-xs">{{ formatDate(inv.invoiceDate) }}</td>
            <td class="text-xs">{{ formatDate(inv.invoiceDateDue) }}</td>
            <td class="text-right">{{ formatMoney(inv.amountTotal) }}</td>
            <td class="text-xs">{{ PAYMENT_STATE_LABELS[inv.paymentState ?? ''] ?? inv.paymentState ?? '—' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty state -->
    <div v-else class="flex flex-col items-center gap-3 py-16 text-base-content/40">
      <DocumentTextIcon class="size-12" />
      <p>Aucune facture trouvée pour ces filtres</p>
    </div>
  </div>

  <div v-else-if="rows.length" class="overflow-x-auto rounded-xl border border-base-300">
    <table class="table table-sm w-full">
      <caption class="sr-only">Suivi de facturation des devis signés</caption>
      <thead>
        <tr>
          <th class="w-8" />
          <th>Réf. devis</th>
          <th>Réf. client</th>
          <th class="cursor-pointer select-none hover:bg-base-200" @click="toggleSort('companyName')">Client{{ sortIndicator('companyName') }}</th>
          <th>Commercial</th>
          <th class="cursor-pointer select-none hover:bg-base-200" @click="toggleSort('signedAt')">Signé le{{ sortIndicator('signedAt') }}</th>
          <th class="text-right cursor-pointer select-none hover:bg-base-200" @click="toggleSort('totalCa')">Montant devis{{ sortIndicator('totalCa') }}</th>
          <th class="text-right cursor-pointer select-none hover:bg-base-200" @click="toggleSort('billedAmount')">Montant facturé{{ sortIndicator('billedAmount') }}</th>
          <th>Émission facture</th>
          <th>Échéance</th>
          <th class="cursor-pointer select-none hover:bg-base-200" @click="toggleSort('billingStatus')">Facturation{{ sortIndicator('billingStatus') }}</th>
          <th class="cursor-pointer select-none hover:bg-base-200" @click="toggleSort('paymentStatus')">Paiement{{ sortIndicator('paymentStatus') }}</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="row in sortedRows" :key="row.quoteUuid">
          <tr class="hover cursor-pointer" @click="toggle(row.quoteUuid)">
            <td>
              <button class="btn btn-ghost btn-xs" :disabled="row.invoices.length === 0">
                <ChevronDownIcon v-if="expanded.has(row.quoteUuid)" class="size-4" />
                <ChevronRightIcon v-else class="size-4" />
              </button>
            </td>
            <td class="font-mono text-xs">{{ row.quoteReference }}</td>
            <td class="font-mono text-xs">{{ row.customerReference || '—' }}</td>
            <td>{{ row.companyName || '—' }}</td>
            <td>{{ row.salesEngineerName || '—' }}</td>
            <td class="text-xs">{{ formatDate(row.signedAt) }}</td>
            <td class="text-right">{{ formatMoney(row.totalCa) }}</td>
            <td class="text-right">{{ formatMoney(row.billedAmount) }}</td>
            <td class="text-xs">{{ invoiceDateRange(row) }}</td>
            <td class="text-xs">{{ formatDate(row.invoiceDateDueMax) }}</td>
            <td>
              <span :class="['badge badge-sm h-auto whitespace-normal text-center leading-tight py-1', BILLING_CONFIG[row.billingStatus].class]">
                {{ BILLING_CONFIG[row.billingStatus].label }}
              </span>
            </td>
            <td>
              <!-- Pas de devis facturé → "Non payé" serait redondant avec le badge Facturation -->
              <span
                v-if="row.billingStatus !== 'NOT_INVOICED'"
                :class="['badge badge-sm h-auto whitespace-normal text-center leading-tight py-1', PAYMENT_CONFIG[row.paymentStatus].class]"
              >
                {{ PAYMENT_CONFIG[row.paymentStatus].label }}
              </span>
              <span v-else class="text-xs opacity-30">—</span>
            </td>
          </tr>

          <!-- Factures liées -->
          <tr v-if="expanded.has(row.quoteUuid)" class="bg-base-200/50">
            <td />
            <td colspan="11" class="py-2">
              <div v-if="row.invoices.length === 0" class="text-xs text-base-content/50 italic">
                Aucune facture rattachée à ce devis.
              </div>
              <table v-else class="table table-xs w-full">
                <thead>
                  <tr class="text-base-content/60">
                    <th>Facture</th>
                    <th>Type</th>
                    <th>État</th>
                    <th>Date</th>
                    <th>Échéance</th>
                    <th class="text-right">Montant</th>
                    <th>Paiement</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="inv in row.invoices" :key="inv.uuid">
                    <td class="font-mono text-xs">
                      <DocumentTextIcon class="size-3 inline mr-1 text-base-content/40" />{{ inv.name || inv.referenceExterne || '—' }}
                    </td>
                    <td class="text-xs">{{ inv.moveType || '—' }}</td>
                    <td class="text-xs">{{ inv.state || '—' }}</td>
                    <td class="text-xs">{{ formatDate(inv.invoiceDate) }}</td>
                    <td class="text-xs">{{ formatDate(inv.invoiceDateDue) }}</td>
                    <td class="text-right text-xs">{{ formatMoney(inv.amountTotal) }}</td>
                    <td class="text-xs">{{ PAYMENT_STATE_LABELS[inv.paymentState ?? ''] ?? inv.paymentState ?? '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>

  <!-- Empty state -->
  <div v-else class="flex flex-col items-center gap-3 py-16 text-base-content/40">
    <DocumentTextIcon class="size-12" />
    <p>Aucun devis signé trouvé pour ces filtres</p>
  </div>
</template>
